// ========================================
// Check-in History Component
// ========================================
// View past check-ins with data visualization

import { useMemo } from 'react';
import { format } from 'date-fns';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useCheckIns } from '../../hooks/useCheckIns';
import { CORE_EMOTIONS, getEmotionColor } from '../../utils/emotionData';
import './CheckinHistory.css';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CheckinHistory = () => {
  const { checkIns, loading } = useCheckIns();

  // Prepare chart data
  const chartData = useMemo(() => {
    const emotionCounts: Record<string, number> = {};

    // Initialize counts
    CORE_EMOTIONS.forEach((emotion) => {
      emotionCounts[emotion] = 0;
    });

    // Count emotions across all check-ins
    checkIns.forEach((checkIn) => {
      Object.values(checkIn.emotions).forEach((emotions) => {
        emotions?.forEach((emotion: string) => {
          emotionCounts[emotion] = (emotionCounts[emotion] || 0) + 1;
        });
      });
    });

    return {
      labels: [...CORE_EMOTIONS],
      datasets: [
        {
          label: 'Number of times selected',
          data: CORE_EMOTIONS.map((emotion) => emotionCounts[emotion]),
          backgroundColor: CORE_EMOTIONS.map((emotion) => getEmotionColor(emotion)),
          borderColor: CORE_EMOTIONS.map((emotion) => getEmotionColor(emotion)),
          borderWidth: 1,
        },
      ],
    };
  }, [checkIns]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Emotional Patterns Over Time',
        color: 'white',
        font: {
          size: 16,
          weight: 'bold' as const,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: 'rgba(255, 255, 255, 0.8)',
          stepSize: 1,
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
      x: {
        ticks: {
          color: 'rgba(255, 255, 255, 0.8)',
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
  };

  if (loading) {
    return (
      <div className="history-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading check-in history...</p>
        </div>
      </div>
    );
  }

  if (checkIns.length === 0) {
    return (
      <div className="history-container">
        <div className="history-content">
          <div className="empty-state glass-panel">
            <h2>No Check-ins Yet</h2>
            <p>Start your first check-in to see your emotional patterns over time.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="history-container">
      <div className="history-content">
        {/* Visualization Section */}
        <section className="visualization-section glass-panel">
          <div className="chart-container">
            <Bar data={chartData} options={chartOptions} />
          </div>
        </section>

        {/* Check-ins List */}
        <section className="checkins-list-section">
          <h2 className="section-title">Recent Check-ins</h2>

          <div className="checkins-list">
            {checkIns.map((checkIn) => {
              const timestamp = checkIn.timestamp;
              const formattedDate = timestamp
                ? format(
                    typeof timestamp === 'object' && 'toDate' in timestamp
                      ? timestamp.toDate()
                      : new Date(timestamp),
                    'MMM d, yyyy h:mm a'
                  )
                : 'Unknown date';

              const totalEmotions = Object.values(checkIn.emotions).reduce(
                (sum, emotions) => sum + (emotions?.length || 0),
                0
              );

              return (
                <div key={checkIn.id} className="checkin-item glass-card">
                  <div className="checkin-item-header">
                    <span className="checkin-date">{formattedDate}</span>
                    <span className="checkin-count">{totalEmotions} emotions</span>
                  </div>

                  <div className="checkin-emotions">
                    {Object.entries(checkIn.emotions).map(([category, emotions]) => {
                      if (!emotions || emotions.length === 0) return null;

                      return (
                        <div key={category} className="emotion-category">
                          <span className="category-label">{category}:</span>
                          <div className="emotion-tags">
                            {emotions.map((emotion: string, index: number) => (
                              <span
                                key={index}
                                className="emotion-tag"
                                style={{ backgroundColor: getEmotionColor(emotion) }}
                              >
                                {emotion}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {checkIn.notes && (
                    <div className="checkin-notes">
                      <p>{checkIn.notes}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default CheckinHistory;
